import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Calculator,
  Shield,
  Users,
  TrendingUp,
  Star,
  CheckCircle,
  Calendar,
  Play,
} from "lucide-react";

// YouTube video testimonials
const youtubeVideos = [
  { id: "AckVuB84FNo", title: "Témoignage Alexandre" },
  { id: "TonsXr2vJXQ", title: "Témoignage Johan" },
];

const features = [
  {
    icon: Calculator,
    title: "Développement",
    description: "Lancement et développement de votre activité : négociation employeur, dossier France Travail, création d'entreprise, passage de micro à société.",
  },
  {
    icon: Shield,
    title: "Gestion complète",
    description: "Gestion administrative, financière et juridique de votre activité. Comptabilité, déclarations, contrats, nous nous occupons de tout.",
  },
  {
    icon: TrendingUp,
    title: "Optimisation 360°",
    description: "Optimisation de votre rémunération, réduction des charges sociales, optimisations fiscales (IS, IR) et financières.",
  },
  {
    icon: Users,
    title: "Diversification",
    description: "Stratégie d'investissement, création de holding, structuration de side-business et accès à notre Club Deal exclusif.",
  },
];

const stats = [
  { value: "100%", label: "Gagnent plus" },
  { value: "100%", label: "Paient moins" },
  { value: "360°", label: "Accompagnement" },
  { value: "0€", label: "Surprise cachée" },
];

const testimonials = [
  {
    name: "Mehdi",
    role: "Data Engineer",
    content: "Après un an et demi en micro, j'ai basculé en société avec Freelanceurs.io. Impôt sur le revenu divisé par 3 et premier projet immobilier en SCI lancé en moins de 12 mois grâce à ma holding.",
    rating: 5,
  },
  {
    name: "Isis",
    role: "Product Owner",
    content: "Rupture conventionnelle négociée, dossier ARE monté, SAS créée et optimisée. J'ai même obtenu l'accord de France Travail pour les dividendes avec l'ARE : +12K€ d'économies !",
    rating: 5,
  },
  {
    name: "Benjamin",
    role: "Consultant IT",
    content: "Freelanceurs s'est occupé de tout, la gestion, l'optimisation et même la stratégie d'investissement. C'est très confortable au quotidien pour me focus sur ma mission.",
    rating: 5,
  },
];

export default function HomePage() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-pattern relative py-20 md:py-32 lg:py-40" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium animate-fade-in">
                  <span className="w-2 h-2 bg-[#52fbb0] rounded-full animate-pulse"></span>
                  Freelanceurs.io - Ton partenaire sérénité
                </div>
                <h1 className="font-manrope font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-tight animate-fade-in-up">
                  Plus de{" "}
                  <span className="relative">
                    <span className="gradient-text">business</span>
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                      <path d="M2 6C50 2 150 2 198 6" stroke="#52fbb0" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                  </span>
                  , moins de gestion
                </h1>
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed animate-fade-in-up delay-200">
                  En déléguant votre activité, offrez vous le confort d'optimiser au maximum et de profiter de l'essentiel : vous, vos proches et votre business.
                  <span className="block mt-2">Accompagnement 360° pour freelances.</span>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
                <Link to="/services" data-testid="hero-cta-services">
                  <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-8 py-6 text-lg shadow-lg shadow-green-200/50 hover:shadow-green-300/50 hover:-translate-y-0.5 transition-all">
                    Découvrir nos services
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/simulateur" data-testid="hero-cta-simulateur">
                  <Button variant="outline" className="border-2 border-slate-200 hover:border-slate-900 font-semibold rounded-full px-8 py-6 text-lg transition-all">
                    Lancer le simulateur
                  </Button>
                </Link>
              </div>

              {/* Stats inline */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-slate-200 animate-fade-in-up delay-400">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center md:text-left">
                    <div className="font-manrope font-bold text-2xl md:text-3xl text-slate-900">{stat.value}</div>
                    <div className="text-slate-500 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual */}
            <div className="relative hidden lg:block animate-fade-in delay-300">
              <div className="relative z-10">
                <img
                  src="https://images.pexels.com/photos/3194518/pexels-photo-3194518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Freelancer travaillant"
                  className="rounded-3xl shadow-2xl shadow-slate-200/50"
                />
              </div>
              {/* Floating cards */}
              <div className="absolute -left-8 top-1/4 bg-white p-4 rounded-2xl shadow-xl animate-float z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#52fbb0]/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-[#52fbb0]" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">+32%</div>
                    <div className="text-xs text-slate-500">Revenus nets</div>
                  </div>
                </div>
              </div>
              <div className="absolute -right-4 bottom-1/4 bg-white p-4 rounded-2xl shadow-xl animate-float delay-500 z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#52fbb0]/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-[#52fbb0]" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Optimisé</div>
                    <div className="text-xs text-slate-500">Statut validé</div>
                  </div>
                </div>
              </div>
              {/* Background decoration */}
              <div className="absolute -z-10 -right-20 -top-20 w-72 h-72 bg-[#52fbb0]/10 rounded-full blur-3xl"></div>
              <div className="absolute -z-10 -left-10 -bottom-10 w-48 h-48 bg-[#52fbb0]/5 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Accompagnement{" "}
              <span className="gradient-text">360°</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Du développement à la diversification, nous vous accompagnons à chaque étape de votre parcours freelance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="feature-card border-0 bg-slate-50 hover:bg-white group animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#52fbb0]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#52fbb0]/20 transition-colors">
                    <feature.icon className="h-9 w-9 text-[#52fbb0]" />
                  </div>
                  <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Simulator CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900 relative overflow-hidden" data-testid="simulator-cta-section">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#52fbb0] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#52fbb0] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Trouvez le statut idéal
              <br />
              <span className="text-[#52fbb0]">en 5 minutes</span>
            </h2>
            <p className="text-slate-300 text-lg md:text-xl mb-10">
              Micro-entreprise, EURL, SASU ou portage salarial ? Notre simulateur compare tous les statuts 
              et vous guide vers le meilleur choix selon votre situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/simulateur" data-testid="cta-simulateur-main">
                <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-10 py-6 text-lg shadow-lg shadow-green-500/20 hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all animate-pulse-glow">
                  <Calculator className="mr-2 h-5 w-5" />
                  Comparer les statuts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Ils ont rejoint Freelanceurs.io
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Découvrez les retours de freelances que nous accompagnons au quotidien.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="testimonial-card border-0 shadow-lg animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-8 pt-12">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-[#52fbb0] text-[#52fbb0]" />
                    ))}
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#52fbb0]/20 rounded-full flex items-center justify-center">
                      <span className="font-semibold text-[#52fbb0]">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Feedbacks Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="video-feedbacks-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Play className="h-4 w-4" />
              Feedbacks vidéo
            </div>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Nos freelances témoignent
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Découvrez en vidéo les retours d'expérience de nos freelances accompagnés.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {youtubeVideos.map((video, index) => (
              <div
                key={video.id}
                className="aspect-video rounded-2xl overflow-hidden shadow-xl animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`video-feedback-${index}`}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/feedbacks" data-testid="see-all-feedbacks">
              <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-semibold border-2">
                Voir tous les témoignages
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 md:p-16 lg:p-20 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#52fbb0]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#52fbb0]/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
                Prêt à rejoindre Freelanceurs.io ?
              </h2>
              <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                Déléguez et offrez vous le confort d'optimiser et de profiter de l'essentiel.
                <span className="block mt-2">Ton partenaire sérénité.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/rendez-vous"
                  data-testid="cta-rdv-main"
                >
                  <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-10 py-6 text-lg shadow-lg hover:-translate-y-0.5 transition-all">
                    <Calendar className="mr-2 h-5 w-5" />
                    Prendre rendez-vous
                  </Button>
                </Link>
                <Link to="/contact" data-testid="cta-contact">
                  <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold rounded-full px-10 py-6 text-lg transition-all">
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
